import { gsap } from "gsap";

import { AbstractReel } from "../../../abstracts/elements/AbstractReel";
import { AbstractSymbol } from "../../../abstracts/elements/AbstractSymbol";
import { AbstractView } from "../../../abstracts/modules/AbstractView";
import { AbstractWinScreen } from "../../../abstracts/modules/screens/AbstractWinScreen";
import { LevelName, TimelineLabels, WinType } from "../../../abstracts/types/enums";
import { ITotalWinDecoratorData, IWinLevel } from "../../../abstracts/types/interfaces";
import { getTotalFromArrayByPropertyName } from "../../../abstracts/utils/JSUtils";
import { playSound, playSoundOnce } from "../../../abstracts/utils/SoundUtils";
import { UCSoundNames } from "../../assetsLoader/UCAssetsNames";
import UCReel from "../../modules/Reels/UCReel";
import { UCSymbol, isFish } from "../../modules/Reels/symbols/UCSymbol";
import { UCSymbolNames } from "../../types/UCEnums";

export const totalWinDecorator = (data: ITotalWinDecoratorData): void => {
    const {
        winLevels = [],
        winAmount = 0,
        timeline = gsap.timeline(),
        winScreen = new AbstractWinScreen(`WinScreen`, new AbstractView()),
        betValue = 1,
        finishTextAnimationDuration = 1.5,
        finishSpineAnimationDuration = 0.6,
        winIsBelowStake = false,
        reels = [],
        winSymbolsPos = [],
        betWayWins = [],
        onDecoratorComplete = () => {},
        onDecoratorStart = () => {},
    } = data;

    if (winAmount === 0) {
        return;
    }

    timeline.addLabel(TimelineLabels.TOTAL_WIN_DECORATOR_START);
    onDecoratorStart();

    // animate symbols start
    timeline.addLabel(
        TimelineLabels.START_SYMBOLS_WIN_ANIMATION,
        TimelineLabels.TOTAL_WIN_DECORATOR_START
    );

    const symbolsWinTimeline = gsap.timeline({
        onStart: () => {
            playSound({ name: UCSoundNames.WIN_SWEEP });
        },
    });

    reels.forEach((reel: AbstractReel) => {
        const visibleSymbols = (reel as UCReel).getAllVisibleStopSymbols();

        if (winSymbolsPos.length > 0) {
            visibleSymbols.forEach((symbol: AbstractSymbol, symbolIndex) => {
                if (winSymbolsPos[reel.reelId][symbolIndex] === false) {
                    symbolsWinTimeline.add((symbol as UCSymbol).getNoWinAnimation(), 0);
                } else {
                    if (isFish(symbol.name as UCSymbolNames)) {
                        symbolsWinTimeline.call(
                            () => {
                                playSoundOnce({
                                    name: UCSoundNames.M4_WIN_SWEEP,
                                    playOnceMinDuration: 0.4,
                                });
                            },
                            undefined,
                            0
                        );
                    }
                    symbolsWinTimeline.add((symbol as UCSymbol).getWinAnimation(), 0);
                }
            });
        }
    });

    timeline.add(symbolsWinTimeline, TimelineLabels.START_SYMBOLS_WIN_ANIMATION);

    // end animate symbols

    const winMultiplier: number = winAmount / betValue;
    let labelTime: number = symbolsWinTimeline.duration();
    let minBigWinIndex: number = winLevels.findIndex(
        (level: IWinLevel) => level.name === LevelName.BIG_WIN_1
    );

    const winLevel: IWinLevel = winLevels.filter(
        (level: IWinLevel) => level.min <= winMultiplier && level.max > winMultiplier
    )[0];

    let winType: WinType = winLevel.winType;

    const noOfCombinations: number = getTotalFromArrayByPropertyName(
        betWayWins,
        `numberOfCombinations`
    );
    const waysWon: number = noOfCombinations > 0 ? noOfCombinations : -1;

    const addSkip = () => {
        const skipToNextLevel = () => {
            const nextLabel: string = timeline.nextLabel();

            timeline.seek(timeline.labels[nextLabel], false);
        };

        data.flowStateController?.setSkipCallback(skipToNextLevel);
    };

    if (!winIsBelowStake) {
        if (winMultiplier < winLevels[minBigWinIndex].min) {
            switch (winType) {
                case WinType.SMALL:
                    winScreen.handleSmallWins({
                        timeline: timeline,
                        level: winLevel,
                        animationDuration: winLevel.animationDuration,
                        callback: () => addSkip(),
                        winAmount: winAmount,
                        winMultiplier: winMultiplier,
                        winType: winType,
                        waysWon: waysWon,
                    });
                    break;

                case WinType.MEDIUM:
                    winScreen.handleMediumWins({
                        timeline: timeline,
                        level: winLevel,
                        animationDuration: winLevel.animationDuration,
                        callback: () => addSkip(),
                        winAmount: winAmount,
                        winMultiplier: winMultiplier,
                        winType: winType,
                        waysWon: waysWon,
                    });
                    break;

                case WinType.LARGE:
                    winScreen.handleLargeWins({
                        timeline: timeline,
                        level: winLevel,
                        animationDuration: winLevel.animationDuration,
                        callback: () => addSkip(),
                        winAmount: winAmount,
                        winMultiplier: winMultiplier,
                        winType: winType,
                        waysWon: waysWon,
                    });
                    break;

                default:
                    winScreen.handleTinyWins({
                        timeline: timeline,
                        level: winLevel,
                        animationDuration: winLevel.animationDuration,
                        callback: () => addSkip(),
                        winAmount: winAmount,
                        winMultiplier: winMultiplier,
                        winType: winType,
                        waysWon: waysWon,
                    });

                    break;
            }
        } else {
            winLevels.forEach((level: IWinLevel, index) => {
                if (index >= minBigWinIndex && winMultiplier >= level.min) {
                    timeline.addLabel(
                        level.name,
                        `${TimelineLabels.TOTAL_WIN_DECORATOR_START}+=${labelTime}`
                    );
                    labelTime += level.animationDuration;
                }
            });

            winLevels.forEach((level: IWinLevel, index) => {
                if (index >= minBigWinIndex && winMultiplier >= level.min) {
                    if (level.name === LevelName.MAX_CAP) {
                        winType = WinType.MAX_CAP;

                        winScreen.handleMaxCap({
                            timeline: timeline,
                            level: level,
                            animationDuration: labelTime,
                            callback: () => {
                                data.flowStateController?.setSkipCallback(undefined);
                            },
                            winAmount: winAmount,
                            winMultiplier: winMultiplier,
                            winType: WinType.MAX_CAP,
                            betAmount: betValue,
                            // waysWon: waysWon,
                        });
                    } else {
                        winScreen.handleBigWins({
                            timeline: timeline,
                            level: level,
                            finalLevel: winLevel,
                            animationDuration: labelTime,
                            callback: () => addSkip(),
                            winAmount: winAmount,
                            winMultiplier: winMultiplier,
                            winType: WinType.BIG,
                            betAmount: betValue,
                        });
                    }
                }
            });
        }

        switch (winType) {
            case WinType.BIG:
                timeline.addLabel(TimelineLabels.ON_BIG_WINS_COMPLETE_START);
                winScreen.onBigWinsComplete({
                    timeline: timeline,
                    winType: winType,
                    winAmount: winAmount,
                    level: winLevel,
                    waysWon: waysWon,
                    callback: () => addSkip(),
                });

                timeline.addLabel(
                    TimelineLabels.TOTAL_WIN_DECORATOR_COMPLETE,
                    TimelineLabels.ON_BIG_WINS_COMPLETE_FINISH
                );
                break;

            case WinType.MAX_CAP: {
                timeline.addLabel(TimelineLabels.ON_MAX_CAP_COMPLETE_START);

                winScreen.onMaxCapComplete({ timeline: timeline, callback: () => addSkip() });

                timeline.call(
                    () => {
                        data.flowStateController?.setSkipCallback(undefined);
                    },
                    undefined,
                    `${TimelineLabels.ON_MAX_CAP_COMPLETE_START}-=0.1`
                );

                timeline.addLabel(
                    TimelineLabels.TOTAL_WIN_DECORATOR_COMPLETE,
                    TimelineLabels.ON_MAX_CAP_COMPLETE_FINISH
                );

                break;
            }
            default:
                timeline.addLabel(TimelineLabels.TOTAL_WIN_DECORATOR_COMPLETE);
        }
    } else {
        timeline.addLabel(TimelineLabels.TOTAL_WIN_DECORATOR_COMPLETE);
    }

    timeline.call(
        () => {
            onDecoratorComplete();

            // reels.forEach((reel: AbstractReel) => {
            //     (reel as UCReel).getAllVisibleStopSymbols().forEach((symbol: AbstractSymbol) => {
            //         (symbol as UCSymbol).removeTint();
            //     });
            // });

            if (!winIsBelowStake) {
                winScreen.finish({
                    winType: winType,
                    timeline: timeline,
                    finishTextAnimationDuration: finishTextAnimationDuration,
                    finishSpineAnimationDuration: finishSpineAnimationDuration,
                    waysWon: waysWon,
                });
            }
        },
        undefined,
        TimelineLabels.TOTAL_WIN_DECORATOR_COMPLETE
    );
};
